/**
 * Step 3 summary statistics and the plain-language interpretation.
 *
 * Brief P2: a language model must never compute or narrate numeric
 * results — so the interpretation is a deterministic template, and
 * every number in it is inserted from the engine's result by these
 * functions. The sentences follow the mockup's three-part shape
 * (where the model helped, what barely moved, use with care) and
 * each claim is computed, not asserted.
 */

import type { DomainResult } from '@sae/engine';

import { fmtCv, fmtPct } from './format.js';

export function directCv(d: DomainResult): number | null {
  if (d.direct === undefined || d.direct.estimate <= 0) return null;
  return d.direct.standardError / d.direct.estimate;
}

export function modelCv(d: DomainResult): number | null {
  if (d.model === undefined) return null;
  return d.reliability.cv ?? d.model.cv;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

export interface CellStats {
  readonly areasWithModel: number;
  readonly modelRange: readonly [number, number] | null;
  readonly directRange: readonly [number, number] | null;
  readonly medianModelCv: number | null;
  readonly medianDirectCv: number | null;
  readonly biggestCorrection: {
    readonly areaId: string;
    readonly direct: number;
    readonly model: number;
    readonly directCv: number | null;
    readonly modelCv: number | null;
  } | null;
  readonly areasImproved: number;
  readonly flaggedCount: number;
  readonly suppressedCount: number;
}

export function cellStats(domains: readonly DomainResult[]): CellStats {
  const withModel = domains.filter((d) => d.model !== undefined);
  const modelValues = withModel.map((d) => d.model!.estimate);
  const directValues = domains
    .filter((d) => d.direct !== undefined)
    .map((d) => d.direct!.estimate);
  const range = (xs: number[]): readonly [number, number] | null =>
    xs.length === 0 ? null : [Math.min(...xs), Math.max(...xs)];

  let biggest: CellStats['biggestCorrection'] = null;
  let improved = 0;
  for (const d of withModel) {
    const dc = directCv(d);
    const mc = modelCv(d);
    if (dc !== null && mc !== null && mc < dc) improved++;
    if (d.direct === undefined) continue;
    const change = Math.abs(d.model!.estimate - d.direct.estimate);
    if (biggest === null || change > Math.abs(biggest.model - biggest.direct)) {
      biggest = {
        areaId: d.domain.areaId,
        direct: d.direct.estimate,
        model: d.model!.estimate,
        directCv: dc,
        modelCv: mc,
      };
    }
  }

  return {
    areasWithModel: withModel.length,
    modelRange: range(modelValues),
    directRange: range(directValues),
    medianModelCv: median(
      withModel.map(modelCv).filter((v): v is number => v !== null),
    ),
    medianDirectCv: median(
      domains.map(directCv).filter((v): v is number => v !== null),
    ),
    biggestCorrection: biggest,
    areasImproved: improved,
    flaggedCount: domains.filter((d) => d.status === 'flagged').length,
    suppressedCount: domains.filter((d) => d.status === 'suppressed').length,
  };
}

/** The interpretation paragraphs for one cell, values inserted. */
export function interpretationParagraphs(
  stats: CellStats,
): readonly string[] {
  const paragraphs: string[] = [];
  const b = stats.biggestCorrection;
  if (b !== null) {
    let first =
      `The model moved ${b.areaId} the most: ${fmtPct(b.direct)} direct ` +
      `to ${fmtPct(b.model)} model`;
    if (b.directCv !== null && b.modelCv !== null) {
      first += `, and its CV fell from ${fmtCv(b.directCv)} to ${fmtCv(b.modelCv)}`;
    }
    first +=
      `. Across the ${stats.areasWithModel} areas with model estimates, ` +
      `${stats.areasImproved} saw their CV fall. Areas with large samples ` +
      'move very little: where the direct estimate was already precise, ' +
      'the model estimate stays close to the direct estimate.';
    paragraphs.push(first);
  }
  if (stats.modelRange !== null && stats.directRange !== null) {
    paragraphs.push(
      `Model estimates run from ${fmtPct(stats.modelRange[0])} to ` +
        `${fmtPct(stats.modelRange[1])}; the direct estimates ran from ` +
        `${fmtPct(stats.directRange[0])} to ${fmtPct(stats.directRange[1])}. ` +
        'Shrinkage pulls the extremes toward the pattern the covariates ' +
        'support, which is why the model range is usually the narrower one.',
    );
  }
  const care: string[] = [];
  if (stats.flaggedCount > 0) {
    care.push(
      `${stats.flaggedCount} area${stats.flaggedCount === 1 ? ' carries' : 's carry'} a reliability flag under the active disclosure preset`,
    );
  }
  if (stats.suppressedCount > 0) {
    care.push(
      `${stats.suppressedCount} ${stats.suppressedCount === 1 ? 'is' : 'are'} suppressed and must not be published`,
    );
  }
  paragraphs.push(
    (care.length > 0 ? `Use with care: ${care.join(', and ')}. ` : '') +
      'Report the CV alongside every rate, and do not rank model ' +
      'estimates as if they were exact.',
  );
  return paragraphs;
}
